import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Button, Alert, AlertDescription, Spinner } from '@cuspar/ui';
import { IPlanChangeResult, ISubscription, StaticPlanTier, STATIC_PLAN_LIMITS } from '@cuspar/ywt.ch.model';
import { subscriptionAPI, IDowngradeValidation } from '../../state/api/subscription';
import { PriceGadget } from '../UI/PriceGadget';
import { Check, AlertTriangle, ArrowUp, ArrowDown, Calendar } from 'lucide-react';
import dayjs from 'dayjs';
import { useAuthStore } from '../../state/authStore';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { ROUTES } from '../../routes';
import { i18nDate } from '../../i18n';
import { Trans, useTranslation } from 'react-i18next';
import { YWT_PUBLIC_CONFIG } from '@cuspar/ywt.ch.config/public';

interface PlanChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: ISubscription;
  newTier: StaticPlanTier;
  onSuccess?: () => void;
}

type DialogState = 'loading' | 'upgrade' | 'downgrade' | 'salesClosed' | 'processing' | 'success' | 'error';

export function PlanChangeDialog({ open, onOpenChange, subscription, newTier, onSuccess }: PlanChangeDialogProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<DialogState>('loading');
  const [planChange, setPlanChange] = useState<IPlanChangeResult | null>(null);
  const [validation, setValidation] = useState<IDowngradeValidation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const getActiveSubscriptions = useAuthStore((s) => s.getActiveSubscriptions);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      loadPlanChange();
    }
  }, [open, subscription.id, newTier]);

  async function loadPlanChange() {
    setState('loading');
    setError(null);

    const result = await subscriptionAPI.calculatePlanChange(subscription.id, newTier);
    if (!result.success) {
      setError(result.reason || t('subscription.failedToCalculate'));
      setState('error');
      return;
    }

    setPlanChange(result.data);

    if (result.data.type === 'downgrade') {
      // Also validate downgrade
      const validationResult = await subscriptionAPI.validateDowngrade(subscription.id, newTier);
      if (validationResult.success) {
        setValidation(validationResult.data);
      }
      setState('downgrade');
    } else if (YWT_PUBLIC_CONFIG.staticHosting.salesClosed) {
      setState('salesClosed');
    } else {
      setState('upgrade');
    }
  }

  async function handleCreateUpgradeOrder() {
    if (YWT_PUBLIC_CONFIG.staticHosting.salesClosed) {
      setState('salesClosed');
      return;
    }

    setState('processing');
    setError(null);

    const result = await subscriptionAPI.createUpgradeOrder(subscription.id, newTier);
    if (!result.success) {
      setError(result.reason || t('subscription.failedToCreateOrder'));
      setState('error');
      return;
    }

    if (!result.data.success || !result.data.orderId) {
      setError(result.data.error || t('subscription.failedToCreateOrder'));
      setState('error');
      return;
    }

    // Navigate to checkout order page
    onOpenChange(false);
    navigate(ROUTES.checkout.order.replace(':orderId', result.data.orderId));
  }

  async function handleScheduleDowngrade() {
    setState('processing');
    setError(null);

    const result = await subscriptionAPI.scheduleDowngrade(subscription.id, newTier);
    if (!result.success) {
      setError(result.reason || t('subscription.failedToSchedule'));
      setState('error');
      return;
    }

    if (!result.data.success) {
      setError(result.data.errors?.join('\n') || t('subscription.validationFailed'));
      setState('downgrade');
      return;
    }

    setState('success');
    toast.success(t('subscription.downgradeToast', { tier: newTier, date: dayjs(subscription.currentPeriodEnd).format('MMM D, YYYY') }));
    await getActiveSubscriptions();
    onSuccess?.();
  }

  function handleClose() {
    if (state !== 'processing') {
      onOpenChange(false);
    }
  }

  const currentLimits = STATIC_PLAN_LIMITS[subscription.tier];
  const newLimits = STATIC_PLAN_LIMITS[newTier];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-md'>
        {state === 'loading' && (
          <>
            <DialogHeader>
              <DialogTitle>{t('subscription.calculatingPlanChange')}</DialogTitle>
            </DialogHeader>
            <div className='flex items-center justify-center py-8'>
              <Spinner className='w-8 h-8' />
            </div>
          </>
        )}

        {state === 'upgrade' && planChange && (
          <>
            <DialogHeader>
              <DialogTitle className='flex items-center gap-2'>
                <ArrowUp className='w-5 h-5 text-green-500' />
                {t('subscription.upgradeTo', { tier: newTier })}
              </DialogTitle>
              <DialogDescription>{t('subscription.upgradeImmediate', { tier: newTier })}</DialogDescription>
            </DialogHeader>

            <div className='space-y-4 py-4'>
              {/* Price breakdown */}
              <div className='rounded-lg border bg-muted/30 p-4 space-y-3'>
                <div className='flex justify-between items-center'>
                  <div className='flex flex-col'>
                    <span className='text-sm font-medium'>{t('subscription.planUpgrade', { tier: newTier })}</span>
                    <span className='text-xs text-muted-foreground'>{t('subscription.daysUntil', { days: planChange.remainingDays, date: i18nDate(new Date(subscription.currentPeriodEnd!)) })}</span>
                  </div>
                  <span className='font-bold text-lg'>
                    <PriceGadget price={planChange.prorationAmount || 0} currency='CHF' />
                  </span>
                </div>
                <p className='text-xs text-muted-foreground'>{t('subscription.prorationExplanation', { currentTier: subscription.tier, newTier })}</p>
              </div>

              {/* New features */}
              <div className='space-y-2'>
                <p className='text-sm font-medium'>{t('subscription.whatYouGet')}</p>
                <ul className='text-sm space-y-1 text-muted-foreground'>
                  <li className='flex items-center gap-2'>
                    <Check className='w-4 h-4 text-green-500' />
                    {t('subscription.upToWebsites', { count: newLimits.maxSitesPerAccount, current: currentLimits.maxSitesPerAccount })}
                  </li>
                  <li className='flex items-center gap-2'>
                    <Check className='w-4 h-4 text-green-500' />
                    {t('subscription.upToCustomDomains', { count: newLimits.maxCustomDomains, current: currentLimits.maxCustomDomains })}
                  </li>
                </ul>
              </div>

              <p className='text-xs text-muted-foreground'>{t('subscription.redirectToPayment')}</p>
            </div>

            <DialogFooter>
              <Button variant='outline' onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleCreateUpgradeOrder}>{t('subscription.createOrder')}</Button>
            </DialogFooter>
          </>
        )}

        {state === 'downgrade' && planChange && (
          <>
            <DialogHeader>
              <DialogTitle className='flex items-center gap-2'>
                <ArrowDown className='w-5 h-5 text-amber-500' />
                {t('subscription.downgradeTo', { tier: newTier })}
              </DialogTitle>
              <DialogDescription>{t('subscription.downgradeAtEnd')}</DialogDescription>
            </DialogHeader>

            <div className='space-y-4 py-4'>
              {/* Validation errors */}
              {validation && !validation.valid && (
                <Alert variant='destructive'>
                  <AlertTriangle className='h-4 w-4' />
                  <AlertDescription>
                    <p className='font-medium mb-2'>{t('subscription.cannotDowngradeYet')}</p>
                    <ul className='list-disc list-inside space-y-1 text-sm'>
                      {validation.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Schedule info */}
              <div className='rounded-lg border bg-muted/30 p-4 space-y-3'>
                <div className='flex items-center gap-2 text-sm'>
                  <Calendar className='w-4 h-4 text-muted-foreground' />
                  <Trans i18nKey='subscription.planChangesOn' values={{ date: dayjs(subscription.currentPeriodEnd).format('MMMM D, YYYY') }} components={{ strong: <strong /> }} />
                </div>
                <p className='text-sm text-muted-foreground'>{t('subscription.continueAccess', { tier: subscription.tier })}</p>
              </div>

              {/* What changes */}
              <div className='space-y-2'>
                <p className='text-sm font-medium'>{t('subscription.afterDowngrade')}</p>
                <ul className='text-sm space-y-1 text-muted-foreground'>
                  <li className='flex items-center gap-2'>
                    <span className='w-4 h-4 text-center'>•</span>
                    {t('subscription.maxWebsites', { count: newLimits.maxSitesPerAccount })}
                  </li>
                  <li className='flex items-center gap-2'>
                    <span className='w-4 h-4 text-center'>•</span>
                    {t('subscription.maxCustomDomains', { count: newLimits.maxCustomDomains })}
                  </li>
                </ul>
              </div>

              <p className='text-xs text-muted-foreground'>{t('subscription.noRefund')}</p>
            </div>

            <DialogFooter>
              <Button variant='outline' onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button variant='destructive' onClick={handleScheduleDowngrade} disabled={validation ? !validation.valid : false}>
                {t('subscription.scheduleDowngrade')}
              </Button>
            </DialogFooter>
          </>
        )}

        {state === 'salesClosed' && (
          <>
            <DialogHeader>
              <DialogTitle>{t('hosting.salesClosedTitle')}</DialogTitle>
              <DialogDescription>{t('hosting.salesClosedDescription')}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={handleClose}>{t('common.close')}</Button>
            </DialogFooter>
          </>
        )}

        {state === 'processing' && (
          <>
            <DialogHeader>
              <DialogTitle>{t('common.processing')}</DialogTitle>
            </DialogHeader>
            <div className='flex items-center justify-center py-8'>
              <Spinner className='w-8 h-8' />
            </div>
          </>
        )}

        {state === 'success' && (
          <>
            <DialogHeader>
              <DialogTitle className='flex items-center gap-2'>
                <Check className='w-5 h-5 text-green-500' />
                {planChange?.type === 'upgrade' ? t('subscription.upgradeComplete') : t('subscription.downgradeScheduled')}
              </DialogTitle>
            </DialogHeader>
            <div className='py-4'>
              {planChange?.type === 'upgrade' ? (
                <p className='text-sm text-muted-foreground'>{t('subscription.nowHaveAccess', { tier: newTier })}</p>
              ) : (
                <p className='text-sm text-muted-foreground'>{t('subscription.planWillChange', { tier: newTier, date: dayjs(subscription.currentPeriodEnd).format('MMMM D, YYYY') })}</p>
              )}
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>{t('subscription.done')}</Button>
            </DialogFooter>
          </>
        )}

        {state === 'error' && (
          <>
            <DialogHeader>
              <DialogTitle className='flex items-center gap-2'>
                <AlertTriangle className='w-5 h-5 text-destructive' />
                {t('subscription.somethingWentWrong')}
              </DialogTitle>
            </DialogHeader>
            <div className='py-4'>
              <Alert variant='destructive'>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={handleClose}>
                {t('common.close')}
              </Button>
              <Button onClick={loadPlanChange}>{t('hosting.tryAgain')}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Component to show scheduled downgrade badge with cancel option
 */
export function ScheduledChangeBadge({ subscription, onCancelled }: { subscription: ISubscription; onCancelled?: () => void }) {
  const { t } = useTranslation();
  const [cancelling, setCancelling] = useState(false);
  const getActiveSubscriptions = useAuthStore((s) => s.getActiveSubscriptions);

  if (!subscription.scheduledTier) return null;

  async function handleCancel() {
    setCancelling(true);
    const result = await subscriptionAPI.cancelScheduledChange(subscription.id);
    if (result.success) {
      toast.success(t('subscription.scheduledChangeCancelled'));
      await getActiveSubscriptions();
      onCancelled?.();
    } else {
      toast.error(t('subscription.failedToCancelChange'));
    }
    setCancelling(false);
  }

  return (
    <div className='flex items-center gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30'>
      <Calendar className='w-4 h-4 text-amber-600 dark:text-amber-400' />
      <span className='text-sm'>
        <Trans i18nKey='subscription.changingTo' values={{ tier: subscription.scheduledTier, date: dayjs(subscription.currentPeriodEnd).format('MMM D, YYYY') }} components={{ strong: <strong /> }} />
      </span>
      <Button size='sm' variant='ghost' onClick={handleCancel} loading={cancelling} className='ml-auto text-amber-600 hover:text-amber-700 dark:text-amber-400'>
        {t('common.cancel')}
      </Button>
    </div>
  );
}
